import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Grid, Divider, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ProductFeedback, FeedbackAttachmentFailure } from '../../functions/productFeedbackApi';
import { normaliseInlineImageSrc } from './inlineImageSrc';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FeedbackEditForm from './FeedbackEditForm';

const TYPE_COLORS: Record<string, { main: string; bg: string }> = {
  bug:         { main: '#B91C1C', bg: '#FEE2E2' },
  enhancement: { main: '#6D28D9', bg: '#EDE9FE' },
  feature:     { main: '#1D4ED8', bg: '#DBEAFE' },
};

const PRIORITY_COLORS: Record<string, { main: string; bg: string }> = {
  high:   { main: '#BE123C', bg: '#FFE4E6' },
  medium: { main: '#475569', bg: '#E2E8F0' },
  low:    { main: '#047857', bg: '#D1FAE5' },
};

const STATUS_COLORS: Record<string, { main: string; bg: string }> = {
  open:   { main: '#4338CA', bg: '#E0E7FF' },
  solved: { main: '#065F46', bg: '#D1FAE5' },
};

interface Props {
  ticket: ProductFeedback | null;
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
  /** Show the Solve/Delete action bar. Defaults to `isAdmin` — pass false for view-only admin contexts (e.g. org admin). */
  showActions?: boolean;
  /** Lets the submitter edit their own still-open ticket. Only meaningful when isAdmin is false. */
  canEdit?: boolean;
  onSolve?: (id: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onUpdated?: (updated: ProductFeedback, attachmentFailures?: FeedbackAttachmentFailure[]) => void;
}

const LabelValue: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{value}</Typography>
    </Box>
  );
};

const TicketDetailModal: React.FC<Props> = ({ ticket, open, onClose, isAdmin, showActions, canEdit, onSolve, onDelete, onUpdated }) => {
  const actionsVisible = showActions ?? isAdmin;
  const [solving, setSolving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!ticket) return null;

  // Own still-open ticket: skip the read-only view entirely and edit fields directly.
  const isEditableView = !isAdmin && canEdit && ticket.status === 'open';

  const typeColor     = TYPE_COLORS[ticket.type]     || { main: '#1976d2', bg: '#F5F7F9' };
  const priorityColor = ticket.priority ? PRIORITY_COLORS[ticket.priority] : null;
  const statusColor   = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;

  const handleSolve = async () => {
    if (!onSolve) return;
    setSolving(true);
    try {
      await onSolve(ticket._id);
    } finally {
      setSolving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(ticket._id);
      onClose();
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{ticket.ticketId}</Typography>
            {!isEditableView && (
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.3 }}>{ticket.title}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <Divider />

        {isEditableView ? (
          <FeedbackEditForm
            ticket={ticket}
            onCancel={onClose}
            onSaved={(updated, attachmentFailures) => {
              onUpdated?.(updated, attachmentFailures);
              onClose();
            }}
          />
        ) : (
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* Tags row */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: typeColor.bg, border: `1px solid ${typeColor.main}33`, fontSize: '12px', fontWeight: 500, color: typeColor.main, whiteSpace: 'nowrap' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: typeColor.main, flexShrink: 0 }} />
                    {ticket.type.charAt(0).toUpperCase() + ticket.type.slice(1)}
                </Box>
                {ticket.priority && priorityColor && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: priorityColor.bg, border: `1px solid ${priorityColor.main}33`, fontSize: '12px', fontWeight: 500, color: priorityColor.main, whiteSpace: 'nowrap' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: priorityColor.main, flexShrink: 0 }} />
                    {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                    </Box>
                )}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: statusColor.bg, border: `1px solid ${statusColor.main}33`, fontSize: '12px', fontWeight: 500, color: statusColor.main, whiteSpace: 'nowrap' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: statusColor.main, flexShrink: 0 }} />
                    {ticket.status === 'solved' ? 'Solved' : 'Open'}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              {/* Descriptions are markdown (images pasted into the editor are stored as
                  markdown image tags), so this must render rather than print the raw text. */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px' }}>
                  Description
                </Typography>
                <Box
                  sx={{
                    mt: 0.25, fontSize: 14, overflowWrap: 'anywhere',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block', my: 1 },
                    '& p': { my: 0.5 },
                    '& p:first-of-type': { mt: 0 },
                    '& pre': { bgcolor: '#F5F7F9', p: 1, borderRadius: 1, overflowX: 'auto' },
                    '& code': { bgcolor: '#F5F7F9', px: 0.5, borderRadius: 0.5, fontSize: 13 },
                  }}
                >
                  {/* Same URL repair as the editor preview — a submitted ticket renders
                      the description it was saved with, including any older inline-image
                      URLs that predate the current proxy path. */}
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      img: ({ src, alt, ...rest }) => (
                        <img {...rest} src={normaliseInlineImageSrc(src)} alt={alt || ''} />
                      ),
                    }}
                  >
                    {ticket.description}
                  </ReactMarkdown>
                </Box>
              </Box>
              <LabelValue label="Section / Screen" value={ticket.section} />
              <LabelValue label="Affected URL" value={ticket.affectedUrl} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {isAdmin && (
                <>
                  <LabelValue label="Organisation" value={ticket.orgName} />
                  <LabelValue label="Raised By" value={ticket.userName} />
                  <LabelValue label="Email" value={ticket.userEmail} />
                  {ticket.githubIssueUrl && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px' }}>
                        GitHub Issue
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        <a href={ticket.githubIssueUrl} target="_blank" rel="noopener noreferrer">
                          #{ticket.githubIssueNumber}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              <LabelValue
                label="Submitted"
                value={new Date(ticket.createdAt).toLocaleString()}
              />
            </Grid>

            {/* Images */}
            {ticket.images && ticket.images.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px' }}>
                  Attachments
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                  {ticket.images.map((img, i) => (
                    <Box
                      key={i}
                      component={img.available ? 'a' : 'div'}
                      href={img.available ? img.url : undefined}
                      target={img.available ? '_blank' : undefined}
                      rel={img.available ? 'noopener noreferrer' : undefined}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start',
                        px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                        textDecoration: 'none', color: img.available ? 'text.primary' : 'text.disabled', maxWidth: '100%',
                        '&:hover': img.available ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : undefined,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: img.available ? 'text.secondary' : 'text.disabled' }} />
                      <Typography variant="body2" noWrap sx={{ maxWidth: 320, fontStyle: img.available ? 'normal' : 'italic' }}>
                        {img.name}{!img.available && ' (unavailable)'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        )}

        {!isEditableView && (
        <>
        <Divider />

        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>


          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined" size="small">Close</Button>
            {isAdmin && actionsVisible && (
              <>
                <Button
                  onClick={handleDelete}
                  variant="outlined"
                  size="small"
                  disabled={deleting}
                  startIcon={<DeleteOutlineIcon />}
                  sx={{
                    color: confirmDelete ? '#fff' : '#C62828',
                    borderColor: '#C62828',
                    backgroundColor: confirmDelete ? '#C62828' : 'transparent',
                    '&:hover': { backgroundColor: '#FFEBEE', borderColor: '#C62828' },
                  }}
                  onBlur={() => setConfirmDelete(false)}
                >
                  {deleting ? 'Deleting…' : confirmDelete ? 'Confirm Delete' : 'Delete'}
                </Button>
                <Button
                  onClick={handleSolve}
                  variant="contained"
                  size="small"
                  disabled={ticket.status === 'solved' || solving}
                  sx={{ backgroundColor: '#2E7D32', '&:hover': { backgroundColor: '#1B5E20' } }}
                >
                  {solving ? 'Saving…' : ticket.status === 'solved' ? 'Solved' : 'Mark as Solved'}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
        </>
        )}
      </Dialog>
  );
};

export default TicketDetailModal;